import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { formatDate, getStatusColor, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  ChefHat,
  Clock,
  Check,
  Bell,
  RefreshCw,
  Users,
  Timer,
  UtensilsCrossed,
  ArrowRight,
  CupSoda,
} from "lucide-react";

const KitchenItems = ({ order, qtyColor }) => {
  const kitchenItems = order.items.filter((i) => i.station !== "bar");
  const drinkQty = order.items.filter((i) => i.station === "bar").reduce((s, i) => s + i.quantity, 0);
  return (
    <div className="space-y-3 mb-4">
      {kitchenItems.map((item, index) => (
        <div key={index} className="flex items-start justify-between p-3 rounded-lg bg-slate-800">
          <div>
            <span className={`font-mono text-xl font-bold ${qtyColor} mr-3`}>{item.quantity}x</span>
            <span className="font-semibold">{item.product_name}</span>
            {item.notes && <p className="text-sm text-amber-400 mt-1">Nota: {item.notes}</p>}
          </div>
        </div>
      ))}
      {order.cooking_time && (
        <p className="text-xs text-amber-300 flex items-center gap-1" data-testid={`kds-cooking-time-${order.order_id}`}>
          <Timer className="w-3.5 h-3.5" /> Tempo pedido: {order.cooking_time}
        </p>
      )}
      {drinkQty > 0 && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-800/60 border border-slate-700 text-slate-400 text-sm" data-testid={`kds-drinks-note-${order.order_id}`}>
          <CupSoda className="w-4 h-4 shrink-0" />
          +{drinkQty} bebida(s) — servidas no balcão (não preparar)
        </div>
      )}
    </div>
  );
};

const KitchenDisplayPage = () => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`, {
        headers,
        withCredentials: true,
      });
      // Filter active orders (including ready for pickup); pedidos só de bebidas não vão à cozinha
      const activeOrders = response.data.filter(
        (o) =>
          (o.status === "open" || o.status === "preparing" || o.status === "ready") &&
          o.requires_kitchen !== false
      );
      setOrders(activeOrders);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API_URL}/orders/${orderId}/status?status=${status}`,
        {},
        { headers, withCredentials: true }
      );
      
      if (status === "ready") {
        // Play notification sound
        const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQAz");
        audio.play().catch(() => {});
        toast.success("Pedido pronto!");
      }
      
      fetchOrders();
    } catch (error) {
      toast.error("Erro ao atualizar pedido");
    }
  };

  const getTimeSinceOrder = (createdAt) => {
    const minutes = Math.round((Date.now() - new Date(createdAt)) / 60000);
    return minutes;
  };

  const getTimeColor = (minutes) => {
    if (minutes < 10) return "text-emerald-500";
    if (minutes < 20) return "text-amber-500";
    return "text-red-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const openOrders = orders.filter((o) => o.status === "open");
  const preparingOrders = orders.filter((o) => o.status === "preparing");
  const readyOrders = orders.filter((o) => o.status === "ready");

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="kitchen-display">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-primary">
            <ChefHat className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-['Outfit']">Cozinha</h1>
            <p className="text-slate-400">Display de Pedidos</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-lg px-4 py-2 border-emerald-500 text-emerald-500">
            {openOrders.length} Novos
          </Badge>
          <Badge variant="outline" className="text-lg px-4 py-2 border-amber-500 text-amber-500">
            {preparingOrders.length} Em Preparação
          </Badge>
          <Badge variant="outline" className="text-lg px-4 py-2 border-blue-500 text-blue-500">
            {readyOrders.length} Prontos
          </Badge>
          <Button variant="outline" onClick={fetchOrders} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* New Orders */}
        {openOrders.map((order) => {
          const minutes = getTimeSinceOrder(order.created_at);
          return (
            <Card
              key={order.order_id}
              className="bg-slate-900 border-emerald-500/50 overflow-hidden"
              data-testid={`kitchen-order-${order.order_id}`}
            >
              <div className="h-2 bg-emerald-500" />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-mono text-2xl">#{order.order_number}</CardTitle>
                  <Badge className="bg-emerald-500/20 text-emerald-500">NOVO</Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  {order.table_id && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Mesa
                    </span>
                  )}
                  <span className={`flex items-center gap-1 ${getTimeColor(minutes)}`}>
                    <Timer className="w-4 h-4" />
                    {minutes} min
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <KitchenItems order={order} qtyColor="text-emerald-400" />
                <Button
                  className="w-full h-14 text-lg font-bold bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => updateOrderStatus(order.order_id, "preparing")}
                >
                  <Clock className="w-5 h-5 mr-2" />
                  Iniciar Preparação
                </Button>
              </CardContent>
            </Card>
          );
        })}

        {/* Preparing Orders */}
        {preparingOrders.map((order) => {
          const minutes = getTimeSinceOrder(order.created_at);
          return (
            <Card
              key={order.order_id}
              className="bg-slate-900 border-amber-500/50 overflow-hidden animate-pulse-glow"
              data-testid={`kitchen-order-${order.order_id}`}
            >
              <div className="h-2 bg-amber-500" />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-mono text-2xl">#{order.order_number}</CardTitle>
                  <Badge className="bg-amber-500/20 text-amber-500">PREPARANDO</Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  {order.table_id && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Mesa
                    </span>
                  )}
                  <span className={`flex items-center gap-1 ${getTimeColor(minutes)}`}>
                    <Timer className="w-4 h-4" />
                    {minutes} min
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <KitchenItems order={order} qtyColor="text-amber-400" />
                <Button
                  className="w-full h-14 text-lg font-bold bg-amber-600 hover:bg-amber-700"
                  onClick={() => updateOrderStatus(order.order_id, "ready")}
                >
                  <Check className="w-5 h-5 mr-2" />
                  Marcar como Pronto
                </Button>
              </CardContent>
            </Card>
          );
        })}

        {/* Ready Orders - Waiting for Pickup */}
        {readyOrders.map((order) => {
          const minutes = getTimeSinceOrder(order.created_at);
          return (
            <Card
              key={order.order_id}
              className="bg-slate-900 border-blue-500/50 overflow-hidden"
              data-testid={`kitchen-order-${order.order_id}`}
            >
              <div className="h-2 bg-blue-500 animate-pulse" />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-mono text-2xl">#{order.order_number}</CardTitle>
                  <Badge className="bg-blue-500/20 text-blue-500 animate-pulse">
                    <Bell className="w-3 h-3 mr-1" />
                    PRONTO
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  {order.table_id && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Mesa
                    </span>
                  )}
                  <span className={`flex items-center gap-1 ${getTimeColor(minutes)}`}>
                    <Timer className="w-4 h-4" />
                    {minutes} min
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <KitchenItems order={order} qtyColor="text-blue-400" />
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-center">
                  <Bell className="w-6 h-6 mx-auto text-blue-400 animate-bounce mb-2" />
                  <p className="text-blue-300 font-medium">A aguardar levantamento</p>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Empty State */}
        {orders.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-20">
            <ChefHat className="w-20 h-20 text-slate-700 mb-4" />
            <p className="text-xl text-slate-500">Nenhum pedido pendente</p>
            <p className="text-slate-600">Os novos pedidos aparecerão aqui automaticamente</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KitchenDisplayPage;
